import { Waveform } from "@rosc/ui/v32/recorder-bar";
import { SilenceNotice } from "@rosc/ui/v32/silence-notice";
import { ChevronLeft, Pause, Play } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RecordingConsentCheck } from "@/components/ui/visits/RecordingConsentCheck";
import { RecordingInstructions } from "@/components/ui/visits/RecordingInstructions";
import { useSpeakerLabel } from "@/components/ui/visits/SpeakerTranscript";
import { useLiveShortcutCues } from "@/hooks/visits/useLiveShortcutCues";
import { liveTranscriptLines, withGaps } from "@/lib/live-transcript";
import { cn } from "@/lib/utils";
import {
  type LiveTranscriptGap,
  useTranscriptionStreamingStore,
} from "@/store/transcriptionStreaming.store";
import { LiveTranscriptGapLine } from "./live-transcript-gap";

const clock = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h ? `${h}:` : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
const NO_GAPS: LiveTranscriptGap[] = [];
const ICON_BUTTON =
  "flex h-11 w-11 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring";

/**
 * The new-visit recorder in the v3.2 layout (with recording instructions on):
 * top bar, live transcript, instructions above a floating pill with the
 * waveform, time, pause and Stop. Behaviour is the recorder's own; this only
 * lays it out. Stop offers to write the note or discard the recording; Back
 * asks before discarding.
 */
export const RecorderV32 = ({
  status,
  recordingTime,
  mediaStream,
  isRealtimeEnabled,
  onPause,
  onResume,
  onStop,
  onRequestDiscard,
  previewLevels,
  instructions,
  noteSettings,
  consent = false,
  title,
  subtitle,
  notice,
}: {
  status: string;
  recordingTime: number;
  mediaStream: MediaStream | null;
  isRealtimeEnabled: boolean;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  /** Back: asks before discarding. */
  onRequestDiscard?: () => void;
  previewLevels?: number[];
  /** Recording instructions on (VITE_RECORDING_INSTRUCTIONS_ENABLED). */
  instructions: boolean;
  /** The note template chip: Stop writes the note with this template. */
  noteSettings?: ReactNode;
  /** "Patient consented to recording" (VITE_RECORDING_CONSENT_ENABLED). */
  consent?: boolean;
  /**
   * A recording added to a visit (Add recording, Start visit): the top bar
   * names it, with the recording state under it.
   */
  title?: string;
  subtitle?: string;
  /** A notice for the doctor, shown under the top bar above the transcript. */
  notice?: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const [silent, setSilent] = useState(false);
  const paused = status === "paused";
  const transcript = useTranscriptionStreamingStore(
    (state) => state.transcriptionStreamText,
  );
  const loading = useTranscriptionStreamingStore((state) => state.isLoading);
  // Parts the live transcript missed (screen lock, network drop), shown
  // where they happened: being added again, or a "Paused" marker.
  const gaps = useTranscriptionStreamingStore((state) =>
    isRealtimeEnabled ? state.gaps : NO_GAPS,
  );
  // Words still being recognised: shown after the finished lines, never saved.
  const partial = useTranscriptionStreamingStore((state) =>
    paused ? "" : state.partialText,
  );
  const resetStreamText = useTranscriptionStreamingStore(
    (state) => state.resetStreamText,
  );
  // A new recording starts with an empty live transcript (as the classic
  // recorder's transcript list does).
  useEffect(() => {
    resetStreamText();
  }, [resetStreamText]);
  const cues = useLiveShortcutCues(
    instructions && isRealtimeEnabled,
    recordingTime,
  );
  // Diarized lines carry "Speaker 0:" (the stored format); show the
  // speaker as a label, as the transcript drawer does.
  const lines = useMemo(
    () => liveTranscriptLines(transcript, partial),
    [transcript, partial],
  );
  const lastFinished = lines.filter((line) => !line.partial).length - 1;
  const speakerLabel = useSpeakerLabel();
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [transcript.length, partial, gaps.length]);
  const items = useMemo(() => withGaps(lines, gaps), [lines, gaps]);

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col self-stretch bg-v3-panel font-v3 text-v3-text"
      data-testid="recorder-v32"
    >
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-v3-border px-1.5">
        <button
          aria-label={t("recorderV32.back")}
          className={ICON_BUTTON}
          type="button"
          onClick={onRequestDiscard}
        >
          <ChevronLeft aria-hidden className="h-5 w-5" />
        </button>
        {title ? (
          <div className="flex min-w-0 flex-1 flex-col items-center px-2 text-center">
            <p
              className="max-w-full truncate text-[15px] font-semibold text-v3-text"
              data-testid="recorder-v32-title"
            >
              {title}
            </p>
            <p
              className="flex max-w-full items-center gap-1.5 text-[12.5px] font-medium text-v3-text-3"
              role="status"
            >
              <span
                aria-hidden
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full",
                  paused ? "bg-v3-text-3" : "animate-pulse bg-v3-rec",
                )}
              />
              <span className="truncate">
                {paused ? t("recorderV32.paused") : t("recorderV32.recording")}
                {subtitle ? ` · ${subtitle}` : ""}
              </span>
            </p>
          </div>
        ) : (
          <p
            className="flex items-center gap-2 text-[15px] font-semibold text-v3-text-2"
            role="status"
          >
            <span
              aria-hidden
              className={cn(
                "h-2 w-2 rounded-full",
                paused ? "bg-v3-text-3" : "animate-pulse bg-v3-rec",
              )}
            />
            {paused ? t("recorderV32.paused") : t("recorderV32.recording")}
          </p>
        )}
        {/* Discard lives in Stop's sheet only; this keeps the title centred. */}
        <span aria-hidden className="h-11 w-11" />
      </header>

      {notice && (
        <div className="shrink-0 px-4 pt-3" data-testid="recorder-v32-notice">
          {notice}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-5">
        {isRealtimeEnabled &&
        (transcript.length > 0 || partial || gaps.length > 0) ? (
          <ol className="space-y-5">
            {items.map((item) => {
              if (item.kind === "gap")
                return (
                  <LiveTranscriptGapLine
                    key={`gap-${item.gap.id}`}
                    gap={item.gap}
                  />
                );
              const { line, index } = item;
              // In-progress words: the same shape in the tentative colour
              // with the v3.2 caret.
              return (
                <li
                  key={line.key}
                  data-testid={
                    line.partial && !lines[index - 1]?.partial
                      ? "live-partial"
                      : undefined
                  }
                >
                  <p className="flex items-baseline gap-2">
                    {line.showSpeaker && line.speaker !== null && (
                      <span
                        className={cn(
                          "text-[12.5px] font-semibold",
                          line.assignment && line.assignment.role !== "unknown"
                            ? "text-v3-text"
                            : "text-v3-text-2",
                        )}
                        data-testid="live-speaker"
                      >
                        {speakerLabel(line.speaker, line.assignment)}
                      </span>
                    )}
                    <span className="font-mono text-[12px] tabular-nums text-v3-text-3">
                      {clock(Math.floor(line.startTime))}
                    </span>
                  </p>
                  <p
                    className={cn(
                      "mt-1 text-[17px] leading-[26px]",
                      line.partial
                        ? "text-v3-text-3"
                        : index === lastFinished
                          ? "text-v3-text"
                          : "text-v3-text-2",
                    )}
                  >
                    {line.text}
                    {line.partial && index === lines.length - 1 && (
                      <span
                        aria-hidden
                        className="ml-0.5 inline-block h-[1.05em] w-0.5 animate-pulse rounded-[1px] bg-v3-brand align-[-2px]"
                      />
                    )}
                  </p>
                </li>
              );
            })}
            {!partial && loading && (
              <li aria-hidden className="h-5 w-2 animate-pulse bg-v3-text-3" />
            )}
            <div ref={end} />
          </ol>
        ) : (
          <div className="flex h-full min-h-[160px] flex-col items-center justify-center px-6 text-center">
            <p className="text-[17px] font-semibold text-v3-text">
              {isRealtimeEnabled
                ? t("recorderV32.listening")
                : t("recorderV32.deviceTitle")}
            </p>
            <p className="mt-1 max-w-[280px] text-[14px] leading-5 text-v3-text-3">
              {isRealtimeEnabled
                ? t("recorderV32.listeningHint")
                : t("recorderV32.deviceHint")}
            </p>
          </div>
        )}
      </div>

      <div className="shrink-0 space-y-3 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-1 sm:px-4 sm:pb-4">
        {consent && <RecordingConsentCheck className="px-1" />}
        {instructions ? (
          <RecordingInstructions
            className="px-1"
            cues={cues}
            leading={noteSettings}
          />
        ) : (
          noteSettings && <div className="flex px-1">{noteSettings}</div>
        )}
        {silent && !paused && (
          <SilenceNotice message={t("recorderV32.silentMic")} />
        )}
        <div className="flex h-[60px] items-center gap-2.5 rounded-full bg-v3-panel py-2 pl-4 pr-2 shadow-v3-float ring-1 ring-v3-border">
          <Waveform
            className="min-w-0 flex-1"
            label={t("recorderV32.micLevel")}
            paused={paused}
            levels={previewLevels}
            onSilentChange={setSilent}
            stream={mediaStream}
          />
          <p className="flex shrink-0 items-center gap-1.5 font-mono text-[15px] tabular-nums text-v3-text">
            <span
              aria-hidden
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                paused ? "bg-v3-text-3" : "bg-v3-rec",
              )}
            />
            {clock(recordingTime)}
          </p>
          {/* Pause and Resume for every recording, live or not (clinician
              feedback: Pause was missing with live OpenAI transcription). */}
          <button
            aria-label={
              paused ? t("recorderV32.resume") : t("recorderV32.pause")
            }
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-v3-surface-2 text-v3-text transition-colors hover:bg-v3-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            type="button"
            onClick={paused ? onResume : onPause}
          >
            {paused ? (
              <Play aria-hidden className="h-4 w-4 fill-current" />
            ) : (
              <Pause aria-hidden className="h-4 w-4 fill-current" />
            )}
          </button>
          <button
            className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-v3-rec-tint px-4 text-[15px] font-semibold text-v3-rec-text transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            type="button"
            onClick={onStop}
          >
            <span aria-hidden className="h-3 w-3 rounded-[3px] bg-v3-rec" />
            {t("recorderV32.stop")}
          </button>
        </div>
      </div>
    </div>
  );
};
